import { useMemo, useState } from 'react';
import { useLessonEarnings, useTutorApplications } from '../../api/tutor-applications';
import { Page, PageHeader } from '../../components/Layout';
import { FilterBar, FilterSelect, SearchInput } from '../../components/FilterBar';
import { Pagination } from '../../components/Pagination';
import { ApplicationStatusBadge, APPLICATION_STATUS_LABEL, Badge } from '../../components/Badge';
import { UserCell } from '../../components/Avatar';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { SkeletonRows } from '../../components/Spinner';
import { useDebounced } from '../../hooks/useDebounced';
import { formatRelative } from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { TutorApplicationStatus } from '../../types/api';
import { ReviewDrawer } from './ReviewDrawer';
import { NoShowReview } from './NoShowReview';

/**
 * The statuses worth a tab, left to right in the order a tutor moves
 * through them. `draft` is absent: a half-filled form is not a queue
 * anybody works, and it is still reachable from the filter.
 */
const TABS: TutorApplicationStatus[] = [
  'submitted',
  'changes_requested',
  'approved',
  'rejected',
  'suspended',
];

/** Prefilled on the approve dialog; the admin still has to confirm it. */
const DEFAULT_COMMISSION_PERCENT = 20;

export function TutorApplicationsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TutorApplicationStatus>('submitted');
  const [page, setPage] = useState(1);
  const [reviewing, setReviewing] = useState<string | null>(null);

  const debouncedSearch = useDebounced(search);

  const query = useMemo(
    () => ({ page, limit: 20, status, q: debouncedSearch.trim() || undefined }),
    [page, status, debouncedSearch],
  );

  const applications = useTutorApplications(query);
  // Only for the count on the tab: the list itself lives further down.
  const noShows = useLessonEarnings({ status: 'pending_review', limit: 1 });

  return (
    <Page>
      <PageHeader
        title="Tutor applications"
        description="Identity, teaching details and payout details, checked before a tutor can be given a student."
      />

      <div className="panel overflow-hidden">
        <FilterBar
          onReset={() => {
            setSearch('');
            setStatus('submitted');
            setPage(1);
          }}
          hasFilters={Boolean(debouncedSearch) || status !== 'submitted'}
        >
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search the tutor"
          />
          <FilterSelect
            label="Status"
            value={status}
            onChange={(v) => {
              setStatus((v || 'submitted') as TutorApplicationStatus);
              setPage(1);
            }}
            placeholder="Awaiting review"
            options={TABS.concat('draft').map((s) => ({
              value: s,
              label: APPLICATION_STATUS_LABEL[s],
            }))}
          />
        </FilterBar>

        {applications.isError ? (
          <ErrorState
            message={
              applications.error instanceof ApiError ? applications.error.message : undefined
            }
            onRetry={() => void applications.refetch()}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tutor</th>
                    <th>Status</th>
                    <th>ID</th>
                    <th>Payout</th>
                    <th>Commission</th>
                    <th>Waiting</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {applications.isLoading ? (
                    <SkeletonRows rows={8} cols={7} />
                  ) : applications.data && applications.data.data.length > 0 ? (
                    applications.data.data.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <UserCell name={a.fullName} src={a.avatarUrl} />
                          <span className="mt-0.5 block text-xs text-ink-3">{a.email}</span>
                        </td>
                        <td>
                          <ApplicationStatusBadge status={a.applicationStatus} />
                          {/* The flag that matters operationally: approved,
                              still teaching, and not able to take anything
                              new until an admin completes their file. */}
                          {a.missingDocuments && (
                            <span className="mt-1 block">
                              <Badge tone="danger">Missing documents</Badge>
                            </span>
                          )}
                        </td>
                        <td className="text-xs text-ink-2">
                          {a.documentCount > 0 ? `${a.documentCount} on file` : '—'}
                        </td>
                        <td className="text-xs text-ink-2">{a.hasPayoutAccount ? 'Yes' : '—'}</td>
                        <td className="text-xs tabular-nums text-ink-2">
                          {a.commissionPercent === undefined ? '—' : `${a.commissionPercent}%`}
                        </td>
                        <td className="text-xs text-ink-3">
                          {a.submittedAt ? formatRelative(a.submittedAt) : '—'}
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setReviewing(a.id)}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          title="Nothing here"
                          description={
                            status === 'submitted'
                              ? 'No applications are waiting for a decision.'
                              : 'No tutors in this state.'
                          }
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {applications.data && (
              <Pagination meta={applications.data.meta} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      {/* Below the queue rather than on its own screen: a reviewer working
          the applications is the same person who settles a disputed
          no-show, and both are "a tutor is waiting on us". */}
      <div className="mt-6">
        <NoShowReview count={noShows.data?.meta.total ?? 0} />
      </div>

      {reviewing && (
        <ReviewDrawer
          tutorId={reviewing}
          defaultCommissionPercent={DEFAULT_COMMISSION_PERCENT}
          onClose={() => setReviewing(null)}
        />
      )}
    </Page>
  );
}
